package org.ssoft.common.ui.wheel

import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.*
import androidx.compose.ui.graphics.drawscope.*
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.text.TextMeasurer
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Density
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.unit.sp
import apps.App
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.mapNotNull
import kotlinx.coroutines.isActive
import org.ssoft.common.ui.toRadian
import srializable.JSON_INSTANCE
import tk.mallumo.utils.mutableStateFlowOf
import kotlin.math.cos
import kotlin.math.roundToInt
import kotlin.math.sin

internal class InternalWheelCircleGenerator(
    private val loadSvg: suspend (id: Long) -> Painter?,
    private val loadWebp: suspend (id: Long) -> ImageBitmap?,
    private val display: Pair<Int, Int> = 1920 to 1080
) : AppWheelFactory.WheelCircleGenerator {
    private var syncEntryLast: String? = null
    private val stack = mutableStateFlowOf<String?>(null)


    override suspend fun launchCollector(
        fontResolver: FontFamily.Resolver,
        onNewBitmap: (ImageBitmap) -> Unit
    ) = coroutineScope {
        stack.mapNotNull { it }
            .collectLatest {
                if (isActive && syncEntryLast != it) {
                    syncEntryLast = it
                    val config = JSON_INSTANCE.decodeFromString<App.Wheel.Content>(syncEntryLast!!)
                    val newImage = generate(fontResolver, config)
                    onNewBitmap(newImage)
                    delay(10)
                }
            }
    }

    override fun postTask(config: App.Wheel.Content) {
        JSON_INSTANCE.encodeToString<App.Wheel.Content>(config)
            .takeIf { stack.value != it }
            ?.also { stack.value = it }
    }

    override suspend fun generate(
        fontResolver: FontFamily.Resolver,
        config: App.Wheel.Content,
    ): ImageBitmap {
        val wSize = minOf(display.first, display.second)
            .toFloat()
            .let { it * (config.wheel.scale.toFloat() / 100F) }

        val wCenter = wSize / 2F
        val itemRot = 360F / config.items.size.toFloat()
        val itemRotHalf = itemRot / 2F
        val image = ImageBitmap(wSize.roundToInt(), wSize.roundToInt())
        val dividerSize = wSize * 0.01F
        val dividerSizeHalf = dividerSize / 2F
        val iconMaxWidth = wSize / 4F

        val brandingSizeCircle = wSize * (config.branding.scaleCircle.toFloat() / 100F)
        val brandingSizeImage = brandingSizeCircle * (config.branding.scaleImage.toFloat() / 100F)
        val brandingSizeImageH = brandingSizeImage / 2F


        // c= √(a²+b² - 2*a*b*cos(y))
//        val c = sqrt(a.pow(2F) + b.pow(2F) - 2F * a * b * cos(itemRot.toRadian()))
        val cX = wCenter + ((wCenter * sin(itemRot.toRadian())))
        val cY = wCenter - ((wCenter * cos(itemRot.toRadian())))

        val trianglePointC = cX to cY
        val points = listOf(
            wCenter to 0F,
            wCenter to wCenter,
            trianglePointC,
            wSize to trianglePointC.second,
            wSize to 0F,
            wCenter to 0F,
        )


        val textMeasurer = TextMeasurer(
            defaultFontFamilyResolver = fontResolver,
            defaultDensity = Density(1F),
            defaultLayoutDirection = LayoutDirection.Ltr,
            cacheSize = 1
        )

        val clipperWheel = Path().apply {
            addOval(
                Rect(
                    left = dividerSize,
                    top = dividerSize,
                    right = wSize - dividerSize,
                    bottom = wSize - dividerSize
                )
            )
        }
        val clipperBranding = Path().apply {
            addOval(
                Rect(
                    left = wCenter - brandingSizeImageH,
                    top = wCenter - brandingSizeImageH,
                    right = wCenter + brandingSizeImageH,
                    bottom = wCenter + brandingSizeImageH
                )
            )
        }

        CanvasDrawScope().draw(
            density = Density(1F),
            layoutDirection = LayoutDirection.Ltr,
            canvas = Canvas(image),
            size = Size(image.width.toFloat(), image.height.toFloat())
        ) {
            drawCircle(
                color = Color(config.branding.color.surface),
                center = Offset(wCenter, wCenter),
                radius = wCenter
            )
            clipPath(clipperWheel) {
                runCatching {
                    //DRAW triangles
                    config.items
                        .sortedBy { it.position }
                        .forEachIndexed { index, item ->
                            rotate(itemRot * index.toFloat(), center) {
                                drawPath(
                                    Path().apply {
                                        points.forEachIndexed { index, point ->
                                            if (index == 0) moveTo(point.first, point.second)
                                            else lineTo(point.first, point.second)
                                        }
                                        close()
                                    },
                                    color = Color(item.color.surface),
                                    style = Fill
                                )
                            }
                        }
                    //DRAW dividers
                    repeat(config.items.size) { index ->
                        rotate(itemRot * index.toFloat()) {
                            drawRect(
                                topLeft = Offset(
                                    x = wCenter - dividerSizeHalf,
                                    y = 0F
                                ),
                                size = Size(
                                    width = dividerSize,
                                    height = wSize / 2F
                                ),
                                color = Color(config.branding.color.surface),
                                style = Fill,
                            )
                        }
                    }
                    //DRAW text, icons
                    config.items.forEachIndexed { index, item ->
                        rotate(itemRotHalf) {
                            rotate(itemRot * index.toFloat()) {
                                val style = TextStyle(
                                    fontSize = ((wSize / 10F) * (item.fontSize.toFloat() / 100F)).sp,
                                    color = Color(item.color.onSurface),
                                    textAlign = TextAlign.Center
                                )
                                if (item.name.trim().isNotEmpty()) {

                                    val measured = textMeasurer.measure(
                                        text = item.name,
                                        style = style
                                    )

                                    drawText(
                                        textLayoutResult = measured,
                                        topLeft = Offset(
                                            x = wCenter - (measured.size.width / 2F),
                                            y = (wCenter * (item.nameY.toFloat() / 100F)) - (measured.size.height / 2F),
                                        )
                                    )
                                }
                                //iconMaxWidth
                                item.icon.takeIf { it.scale > 1 }
                                    ?.apply {
                                        val targetWidth = iconMaxWidth * (scale.toFloat() / 100F)
                                        takeIf { it.svg > 0 }
                                            ?.let { loadSvg(it.svg) }
                                            ?.apply {
                                                val ratio = targetWidth / intrinsicSize.width
                                                val targetHeight = intrinsicSize.height * ratio

                                                translate(
                                                    left = wCenter - (targetWidth / 2F),
                                                    top = (wCenter * y.toFloat() / 100F) - (targetHeight / 2F)
                                                ) {
                                                    scale(ratio, Offset.Zero) {
                                                        draw(
                                                            size = intrinsicSize,
                                                            colorFilter = ColorFilter.tint(style.color)
                                                        )
                                                    }
                                                }

                                            } ?: takeIf { it.webp > 0 }
                                            ?.let { loadWebp(it.webp) }
                                            ?.also { image ->
                                                val ratio = targetWidth / image.width.toFloat()
                                                val targetHeight = image.height.toFloat() * ratio
                                                translate(
                                                    left = wCenter - (targetWidth / 2F),
                                                    top = (wCenter * y.toFloat() / 100F) - (targetHeight / 2F)
                                                ) {
                                                    scale(ratio, Offset.Zero) {
                                                        drawImage(
                                                            image = image,
                                                            filterQuality = FilterQuality.High
                                                        )
                                                    }
                                                }
                                            }
                                    }

                            }
                        }
                    }
                    //DRAW branding
                    drawCircle(
                        color = Color(config.branding.color.surface),
                        center = Offset(wCenter, wCenter),
                        radius = brandingSizeCircle / 2F
                    )
                    if (config.branding.showBranding) {
                        clipPath(clipperBranding) {

                            loadWebp(config.branding.image)
                                ?.also {
                                    val scale = brandingSizeImage / maxOf(it.width, it.height).toFloat()
                                    translate(
                                        left = wCenter - ((it.width.toFloat() * scale) / 2),
                                        top = wCenter - ((it.height.toFloat() * scale) / 2),
                                    ) {
                                        scale(scale, Offset.Zero) {
                                            drawImage(
                                                image = it,
                                                filterQuality = FilterQuality.High
                                            )
                                        }
                                    }
                                }
                        }
                    }

                }.onFailure {
                    it.printStackTrace()
                }
            }
        }
        return image
    }
}